"use client";

// ── Run samples and custom input: the state behind the buttons (`EX-EDR02`) ──
// Wave 23 decision 6. One hook, two pages: the contest problem and practice
// both hand it their route (`POST .../runs`, `POST .../run`) and draw the same
// buttons, panel and strip from what it holds.
//
// A run is feedback, never a mark (`docs/27` §9.7.8). Since `EX-SRN01` a run
// that finds the judge full waits its turn in the run lane rather than being
// refused, so "running" covers the whole wait and carries when it began, for
// the seconds the page counts. Only a run that waited a full minute comes back
// as `JUDGE_ENGINE_BUSY`, the "slow" state here, with its own Try again and
// never an automatic retry loop. A newer run from another tab taking this
// one's place (`JUDGE_RUN_REPLACED`) is not a failure: the track goes back to
// what it showed before.

import { useCallback, useRef, useState } from "react";

import { ApiError } from "@/lib/api";
import { isRunReplaced } from "../../lib/run-lane";
import type { RunSampleInput, SampleRunResult } from "../../types";
import { isBadRunCase } from "./run-output";

/** Mirrors the backend's `SAMPLE_RUN_STDIN_MAX`, the exam's bound. */
export const RUN_STDIN_MAX = 16 * 1024;

export type RunPhase = "idle" | "running" | "done" | "slow" | "failed";
export type RunTrack = {
  phase: RunPhase;
  result: SampleRunResult | null;
  error: string | null;
  /** The exact source sent, held in memory to avoid marking a newer draft. */
  input?: RunSampleInput;
  /** While running: when Run was pressed, for the seconds the page counts. */
  startedAt?: number;
};
export type RunTab = "samples" | "custom";

const IDLE: RunTrack = { phase: "idle", result: null, error: null };

/** Ctrl + ' (Cmd on a Mac): the run shortcut, in the editor and the input. */
export function isRunShortcut(event: Pick<KeyboardEvent, "ctrlKey" | "metaKey" | "key">): boolean {
  return (event.ctrlKey || event.metaKey) && event.key === "'";
}

export function isJudgeBusyError(err: unknown): boolean {
  return err instanceof ApiError && err.code === "JUDGE_ENGINE_BUSY";
}

export type CodeRuns = {
  diagnosticTab?: RunTab | null;
  samples: RunTrack;
  custom: RunTrack;
  /** The custom input: the first sample's until the reader types. */
  stdin: string;
  setStdin: (value: string) => void;
  /** Which tab the panel shows, `null` while it is closed. */
  panel: RunTab | null;
  setPanel: (tab: RunTab) => void;
  closePanel: () => void;
  toggleCustom: () => void;
  /** The sample the panel compares. */
  selected: number;
  openSample: (index: number) => void;
  /** The strip describes the last sample run rather than the submission. */
  stripShowsRun: boolean;
  /** A submission went out: the strip goes back to describing it. */
  showSubmission: () => void;
  stripOpen: boolean;
  toggleStrip: () => void;
  runSamples: () => void;
  runCustom: () => void;
  /** Ctrl + ': the custom input while its tab is open, else the samples. */
  runShortcut: () => void;
};

export function useCodeRuns({
  run,
  describeError,
  input,
  firstSampleInput,
  openOnRun = false,
}: {
  /** The page's route. */
  run: (input: RunSampleInput) => Promise<SampleRunResult>;
  /** The page's words for a refusal (a contest names its own gates). */
  describeError: (err: unknown) => string;
  /** The draft as it stands, `null` before it has loaded. */
  input: RunSampleInput | null;
  firstSampleInput: string;
  /**
   * `EX-RTW03`: a run-twice problem's samples run in the open panel and it
   * stays open, so its three stages are seen as they come in. Absent on
   * every standard problem, whose panel keeps the canvas's rule below.
   */
  openOnRun?: boolean;
}): CodeRuns {
  const [samples, setSamples] = useState<RunTrack>(IDLE);
  const [diagnosticTab, setDiagnosticTab] = useState<RunTab | null>(null);
  const [custom, setCustom] = useState<RunTrack>(IDLE);
  const [typedStdin, setTypedStdin] = useState<string | null>(null);
  const [panel, setPanelState] = useState<RunTab | null>(null);
  const [selected, setSelected] = useState(0);
  const [stripShowsRun, setStripShowsRun] = useState(false);
  const [stripOpen, setStripOpen] = useState(true);

  // Read through refs, so the handlers below keep one identity for the life
  // of the page and a keystroke does not hand Monaco a new shortcut.
  const runRef = useRef(run);
  runRef.current = run;
  const describeRef = useRef(describeError);
  describeRef.current = describeError;
  const inputRef = useRef(input);
  inputRef.current = input;
  const stdin = typedStdin ?? firstSampleInput;
  const stdinRef = useRef(stdin);
  stdinRef.current = stdin;
  const panelRef = useRef(panel);
  panelRef.current = panel;
  const openOnRunRef = useRef(openOnRun);
  openOnRunRef.current = openOnRun;
  const inFlight = useRef({ samples: false, custom: false });

  const settle = useCallback(
    (err: unknown) =>
      (previous: RunTrack): RunTrack => {
        if (isRunReplaced(err)) {
          return { phase: previous.result ? "done" : "idle", result: previous.result, error: null };
        }
        if (isJudgeBusyError(err)) return { phase: "slow", result: null, error: null };
        return { phase: "failed", result: null, error: describeRef.current(err) };
      },
    [],
  );

  const runSamples = useCallback(() => {
    const draft = inputRef.current;
    if (!draft || !draft.sourceCode.trim() || inFlight.current.samples) return;
    inFlight.current.samples = true;
    setDiagnosticTab("samples");
    setStripShowsRun(true);
    setStripOpen(true);
    setSamples((previous) => ({ phase: "running", result: previous.result, error: null, startedAt: Date.now() }));
    if (openOnRunRef.current) setPanelState("samples");
    const body: RunSampleInput = {
      languageKey: draft.languageKey,
      languageVersion: draft.languageVersion,
      sourceCode: draft.sourceCode,
    };
    void runRef
      .current(body)
      .then((result) => {
        setSamples({ phase: "done", result, error: null, input: body });
        if (!result.compiled) { setPanelState("samples"); return; }
        // The canvas's rule: a sample that went wrong opens the comparison at
        // it; all good closes the comparison, and the strip says so.
        const firstBad = result.results.findIndex(isBadRunCase);
        if (firstBad >= 0) {
          setSelected(firstBad);
          setPanelState("samples");
        } else if (!openOnRunRef.current) {
          setPanelState((open) => (open === "samples" ? null : open));
        }
      })
      .catch((err: unknown) => setSamples(settle(err)))
      .finally(() => {
        inFlight.current.samples = false;
      });
  }, [settle]);

  const runCustom = useCallback(() => {
    const draft = inputRef.current;
    if (!draft || !draft.sourceCode.trim() || inFlight.current.custom) return;
    inFlight.current.custom = true;
    setDiagnosticTab("custom");
    setPanelState("custom");
    setCustom((previous) => ({ phase: "running", result: previous.result, error: null, startedAt: Date.now() }));
    const body: RunSampleInput = {
        languageKey: draft.languageKey,
        languageVersion: draft.languageVersion,
        sourceCode: draft.sourceCode,
        stdin: stdinRef.current.slice(0, RUN_STDIN_MAX),
      };
    void runRef.current(body)
      .then((result) => setCustom({ phase: "done", result, error: null, input: body }))
      .catch((err: unknown) => setCustom(settle(err)))
      .finally(() => {
        inFlight.current.custom = false;
      });
  }, [settle]);

  const runShortcut = useCallback(() => {
    if (panelRef.current === "custom") runCustom();
    else runSamples();
  }, [runCustom, runSamples]);

  const setPanel = useCallback((tab: RunTab) => setPanelState(tab), []);
  const closePanel = useCallback(() => setPanelState(null), []);
  const toggleCustom = useCallback(
    () => setPanelState((open) => (open === "custom" ? null : "custom")),
    [],
  );
  const openSample = useCallback((index: number) => {
    setSelected(index);
    setPanelState("samples");
  }, []);
  const showSubmission = useCallback(() => { setStripShowsRun(false); setDiagnosticTab(null); }, []);
  const toggleStrip = useCallback(() => setStripOpen((open) => !open), []);
  const setStdin = useCallback((value: string) => setTypedStdin(value.slice(0, RUN_STDIN_MAX)), []);

  return {
    diagnosticTab,
    samples,
    custom,
    stdin,
    setStdin,
    panel,
    setPanel,
    closePanel,
    toggleCustom,
    selected,
    openSample,
    stripShowsRun,
    showSubmission,
    stripOpen,
    toggleStrip,
    runSamples,
    runCustom,
    runShortcut,
  };
}
